import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import PrsAlert from './PrsAlert';
import { UamToastRoot } from './UamToastStyles';

const LEAVE_MS = 220;

/**
 * Fixed-position alert that slides in from the bottom and auto-dismisses.
 * `show` drives the lifecycle: false -> true mounts and animates in;
 * true -> false plays the leave animation, then unmounts after LEAVE_MS.
 *
 * Pass an `autoDismissMs` to have the toast call `onClose` on its own; otherwise
 * the consumer is responsible for flipping `show` back to false.
 */
const UamToast = ({
  show, onClose, autoDismissMs, variant, icon, closeAriaLabel, ariaLive, children,
}) => {
  // 'hidden' = not in the DOM; 'entering' = mounted at off-screen baseline so
  // the next paint can transition to 'visible'; 'leaving' = sliding back out.
  const [phase, setPhase] = useState('hidden');

  useEffect(() => {
    if (show) {
      setPhase('entering');
      // Two RAFs so the browser commits the entering styles before we flip to visible.
      let inner = null;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setPhase('visible'));
      });
      return () => {
        cancelAnimationFrame(outer);
        if (inner != null) cancelAnimationFrame(inner);
      };
    }
    if (phase === 'visible' || phase === 'entering') {
      setPhase('leaving');
      const t = setTimeout(() => setPhase('hidden'), LEAVE_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!show || !autoDismissMs || !onClose) return undefined;
    const t = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(t);
  }, [show, autoDismissMs, onClose]);

  if (phase === 'hidden') return null;

  return createPortal(
    <UamToastRoot $phase={phase} role="status" aria-live={ariaLive}>
      <PrsAlert
        variant={variant}
        icon={icon}
        onClose={onClose}
        closeAriaLabel={closeAriaLabel}
      >
        {children}
      </PrsAlert>
    </UamToastRoot>,
    document.body,
  );
};

UamToast.defaultProps = {
  onClose: null,
  autoDismissMs: null,
  variant: null,
  icon: undefined,
  closeAriaLabel: 'Dismiss',
  ariaLive: 'polite',
  children: null,
};

UamToast.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  autoDismissMs: PropTypes.number,
  variant: PropTypes.oneOf(['info', 'success', 'warning', 'danger', 'neutral']),
  icon: PropTypes.node,
  closeAriaLabel: PropTypes.string,
  ariaLive: PropTypes.oneOf(['polite', 'assertive', 'off']),
  children: PropTypes.node,
};

export default UamToast;
